<script setup lang="ts">
import type { AuthTestimonial } from '#auth/composables/useAuthTestimonials'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useAuthTestimonials } from '#auth/composables/useAuthTestimonials'

const props = defineProps<{
  testimonial?: AuthTestimonial
  dismissible?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { testimonial: configuredTestimonial } = useAuthTestimonials()

const active = computed(() => props.testimonial ?? configuredTestimonial.value)

const initials = computed(() => {
  return active.value.name
    .split(' ')
    .map(word => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
})
</script>

<template>
  <figure
    class="relative flex h-full flex-col overflow-hidden rounded-lg bg-cover bg-center p-6 text-white bg-[url(/auth/login-modal-bg.webp)]"
  >
    <div class="flex items-start justify-between">
      <img
        src="/app/logo-light.svg"
        alt="Forgd"
        class="h-[18px] w-auto"
      >
      <button
        v-if="dismissible"
        type="button"
        class="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-md text-white/80 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-label="Close"
        @click="emit('close')"
      >
        <XMarkIcon class="h-5 w-5" />
      </button>
    </div>

    <blockquote class="font-display m-auto flex flex-1 items-center justify-center text-center text-2xl font-normal leading-relaxed w-[408px]">
      &ldquo;{{ active.quote }}&rdquo;
    </blockquote>

    <figcaption class="flex flex-col items-center gap-4 text-center">
      <img
        v-if="active.avatar"
        :src="active.avatar"
        :alt="active.name"
        class="h-14 w-14 shrink-0 rounded-full object-cover"
      >
      <div
        v-else
        class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-semibold"
        aria-hidden="true"
      >
        {{ initials }}
      </div>

      <div class="text-primal-inverted font-display">
        <div>
          {{ active.name }}
        </div>
        <div>
          {{ active.title }}
        </div>
      </div>

      <img
        v-if="active.companyLogo"
        :src="active.companyLogo"
        :alt="active.company"
        class="mt-1 h-5 w-auto max-w-[160px] object-contain"
      >
      <div v-else class="mt-1 text-sm font-semibold">
        {{ active.company }}
      </div>
    </figcaption>
  </figure>
</template>
